import React, { useState } from 'react';

import Control from '../Control/Control';
import Modal from '../Modal/Modal';

// A labelled selector rendered as a single button: the label sits above the
// current value (a pill), and clicking anywhere on the control opens a modal with
// the options stacked in a column. Pass `colors` (option -> colour) to give each
// option a colour-coded left bar (like a song row's difficulty edge), or `icons`
// (option -> node) to show an icon beside each (e.g. mode, theme).
//
// Open state is internal by default; pass `open` + `onOpenChange` to control it
// from outside (e.g. so a song row's difficulty bar can open the filter modal).
const Select = ({
	colors,
	currentOption,
	icons,
	label,
	list,
	onClick,
	onOpenChange,
	open,
	title,
}: SelectTypes) => {
	const [internalOpen, setInternalOpen] = useState(false);
	const isOpen = open ?? internalOpen;

	// A two-option menu (e.g. on/off, title/artist) reads better as two buttons
	// side by side rather than stacked.
	const inlineOptions = list.length === 2;

	const setOpen = (next: boolean) => {
		onOpenChange?.(next);
		if (open === undefined) setInternalOpen(next);
	};

	const handleSelect = (option: string) => {
		onClick(option);
		setOpen(false);
	};

	// Options that have a colour (e.g. the difficulty levels) get a colour-coded
	// left bar; options without one (e.g. "All") get no bar at all.
	const barStyle = (option: string): React.CSSProperties | undefined =>
		colors?.[option]
			? { borderLeft: `6px solid ${colors[option]}` }
			: undefined;

	return (
		<>
			<button className="select" onClick={() => setOpen(true)} type="button">
				<span className="select__label">{label}</span>

				<span className="select__value control" style={barStyle(currentOption)}>
					{icons?.[currentOption] && (
						<span className="select__icon">{icons[currentOption]}</span>
					)}
					{currentOption}
				</span>
			</button>

			{isOpen && (
				<Modal onClose={() => setOpen(false)} title={title}>
					<div
						className={
							inlineOptions
								? 'modal-options modal-options--inline'
								: 'modal-options'
						}
					>
						{list.map((option) => (
							<Control
								active={option === currentOption}
								key={option}
								onClick={() => handleSelect(option)}
								style={barStyle(option)}
							>
								{icons?.[option] && (
									<span className="select__icon">{icons[option]}</span>
								)}
								{option}
							</Control>
						))}
					</div>
				</Modal>
			)}
		</>
	);
};

type SelectTypes = {
	colors?: { [option: string]: string };
	currentOption: string;
	icons?: { [option: string]: React.ReactNode };
	label: string;
	list: string[];
	onClick: (option: string) => void;
	onOpenChange?: (open: boolean) => void;
	open?: boolean;
	title: string;
};

export default Select;
